import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Select } from "common/elements/Select/Select";
import { btnDelay, usePlaySound, useScale } from "common/utils";
import parentI18n from "features/parent/i18n";
import { TitleSvg } from "features/home/assets/TitleSvg";
import { SSO } from "./SSO";
import { useAuthLocale } from "./useAuthLocale";

export const LoginScreen = () => {
  const navigate = useNavigate();
  const playSound = usePlaySound();
  const { width, height, scale } = useScale();
  const { currentLocale, changeLocale, localeOptions, localeLabels } = useAuthLocale();
  const [localeOpen, setLocaleOpen] = useState(false);
  const langBtnRef = useRef<HTMLButtonElement>(null);

  const handleEmailLogin = () => {
    btnDelay(() => {
      playSound();
      navigate("/auth/email-login");
    });
  };

  const handleOpenLink = (url: string) => {
    playSound();
    if ("ReactNativeWebView" in window) {
      window.ReactNativeWebView.postMessage(JSON.stringify({ command: "openLink", data: { url } }));
    } else {
      window.open(url, "_blank");
    }
  };

  const handleToggleLocale = () => {
    btnDelay(() => {
      playSound();
      setLocaleOpen(!localeOpen);
    });
  };

  const handleCloseLocale = () => {
    setLocaleOpen(false);
  };

  const handleChangeLocale = (locale: string) => {
    changeLocale(locale);
  };

  return (
    <div className="flex-col-el w-full h-full bg-gray2-30">
      {/* メインコンテンツ */}
      <div
        style={{ height: height - 52 * scale - 44 * scale }}
        className="flex-col-el items-center justify-start w-full bg-white/40"
      >
        <main
          style={{
            width: width / scale,
            height: (height - 52 * scale - 44 * scale) / scale,
            transform: `scale(${scale})`,
          }}
          className="flex-col-el flex-center shrink-0 p-6 origin-top overflow-y-auto"
        >
          <div
            className="flex-col-view self-center"
            style={{
              width: 828,
              height: 182,
              marginBottom: 14,
            }}
          >
            <TitleSvg />
          </div>

          <div className="flex-col-el flex-center bg-white rounded-[16px] p-8">
            <p className="text-[24px] text-textcolor-black text-center mb-4">
              {parentI18n.t("MSG_SIGN_IN_BTN")}
            </p>
            <div className="flex-col-el flex-center max-w-sm w-full gap-6">
              <button
                className="w-[375px] py-3.5 bg-green-100 text-white rounded-[12px] text-[18px] border-none cursor-pointer hover:opacity-90 transition-all"
                onClick={handleEmailLogin}
              >
                {parentI18n.t("MSG_SIGN_IN_WITH_MAIL")}
              </button>
              <SSO signIn={true} />
            </div>

            {/* 新規登録リンク */}
            <div className="flex-row-el flex-center mt-6">
              <button
                className="bg-white border border-gray2-60 text-textcolor-black rounded-[12px] py-2 px-4 text-[16px] cursor-pointer hover:bg-gray2-20 transition-all"
                onClick={() => {
                  btnDelay(() => {
                    playSound();
                    navigate("/auth/register");
                  });
                }}
              >
                {parentI18n.t("MSG_SIGN_NO_ACCOUNT_REGISTER")}
              </button>
            </div>

            {/* 言語切り替えボタン */}
            <div className="flex-row-el flex-center mt-6">
              <div className="flex-row-el items-center gap-3">
                <span className="text-[20px] text-textcolor-black w-[100px] text-right">
                  {parentI18n.t("MSG_PARENT_PAGE_DISPLAY_LANGUAGE")}
                </span>
                <button
                  ref={langBtnRef}
                  onClick={handleToggleLocale}
                  className="border-0 bg-green-100 flex-row-el items-center gap-2 px-6 py-3 rounded-full backdrop-blur-sm shadow-md cursor-pointer transition-all w-[200px]"
                >
                  <span className="material-symbols-outlined text-white !text-[28px]">
                    language
                  </span>
                  <span className="text-body text-white text-[20px] flex-1 text-center">
                    {localeLabels[currentLocale] || localeLabels["ja_kanji"]}
                  </span>
                  <span className="material-symbols-outlined text-white !text-[20px]">
                    expand_more
                  </span>
                </button>
                <span className="w-[100px]" />
              </div>
            </div>

            <Select
              anchorRef={langBtnRef}
              open={localeOpen}
              onClose={handleCloseLocale}
              value={currentLocale}
              onChange={handleChangeLocale}
              options={[...localeOptions]}
            />
          </div>
        </main>
      </div>

      {/* 最初の画面に戻る */}
      <div style={{ height: 44 * scale }} className="flex-col-el flex-center w-full bg-white/40">
        <button
          onClick={() => {
            btnDelay(() => {
              playSound();
              navigate("/", { replace: true });
            });
          }}
          className="flex-row-el items-center gap-2 text-gray2-100 hover:text-blue-100 transition-all border-none bg-transparent cursor-pointer"
          style={{ transform: `scale(${scale})` }}
        >
          <span className="material-symbols-outlined text-[20px]">arrow_back</span>
          <span className="text-[16px]">{parentI18n.t("MSG_AUTH_BACK_TO_START")}</span>
        </button>
      </div>

      {/* フッター */}
      <div style={{ height: 52 * scale }} className="flex-col-el flex-center w-full bg-white/40">
        <footer
          style={{ transform: `scale(${scale})` }}
          className="flex-row-el flex-center w-[1280px] h-[52px] gap-4 px-6 py-4 text-sm text-gray2-100"
        >
          <span
            className="cursor-pointer hover:text-blue-100 hover:underline transition-colors"
            onClick={() => handleOpenLink("https://inexus-co.com/product/")}
          >
            {parentI18n.t("MSG_SETTING_FOOTER_GO_TO_SITE")}
          </span>
          <span>|</span>
          <span
            className="cursor-pointer hover:text-blue-100 hover:underline transition-colors"
            onClick={() => handleOpenLink("https://inexus-co.com/privacy-policy/")}
          >
            {parentI18n.t("MSG_SETTING_FOOTER_TERMS_OF_SERVICE")}
          </span>
          <span>|</span>
          <span
            className="cursor-pointer hover:text-blue-100 hover:underline transition-colors"
            onClick={() => handleOpenLink("https://inexus-co.com/privacy-policy/")}
          >
            {parentI18n.t("MSG_SETTING_FOOTER_PRIVACY_POLICY")}
          </span>
          <span>|</span>
          <span
            className="cursor-pointer hover:text-blue-100 hover:underline transition-colors"
            onClick={() => handleOpenLink("https://inexus-co.com/contact/")}
          >
            {parentI18n.t("MSG_SETTING_FOOTER_INQUIRY")}
          </span>
        </footer>
      </div>
    </div>
  );
};
